import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import FileUploadComponent from "./FileUploadComponent.tsx";
import "./ModalForm.css";
import { Eventi, Task } from "../lib/interface.ts";
import DeleteModal from "./DeleteModal.tsx";
import { allGet, allPost, allPut } from "../Redux/Actions.tsx";
import { urleventi, urltask } from "../lib/api.ts";
import { useAlert } from "./Alert.tsx";
import { AppDispatch, RootState } from "../Redux/Store/index.ts";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown } from "primereact/dropdown";
import { format } from "date-fns";
import ModalFormTask from "./ModalFormTask.tsx";
import DatePicker, { registerLocale } from "react-datepicker";
import { it } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("it", it);

interface FormEventiProps {
  action: "crea" | "modifica";
  type: any;
  id?: any;
}

const FormEventi: React.FC<FormEventiProps> = ({ action, type, id }) => {
  const [evento, setEvento] = useState<Eventi>({
    id: "",
    titolo: "",
    dataEsecuzione: "",
    descrizione: "",
    dataOra: "", // Data Inizio (campo obbligatorio)
    scadenza: "",
    dataFine: "",
    cantiereId: "",
    contrattoQuadroId: "",
    committenteId: "",
    tasks: [],
  });

  // Stato per gestire errori
  const [errors, setErrors] = useState({
    titolo: false,
  });

  const dispatch: AppDispatch = useDispatch();
  const { token } = useSelector((state: RootState) => state.login);
  const { committenti } = useSelector((state: RootState) => state.committenti);
  const { contratti } = useSelector((state: RootState) => state.contratti);
  const { cantieri } = useSelector((state: RootState) => state.cantieri);
  const { task } = useSelector((state: RootState) => state.task);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState<boolean>(!id);

  const [contrattoSearchText, setContrattoSearchText] = useState("");
  const [cantiereSearchText, setCantiereSearchText] = useState("");
  const [committenteSearchText, setCommittenteSearchText] = useState("");

  const showAlert = useAlert();
  const navigate = useNavigate();

  const dateToString = (date: Date) => format(date, "yyyy-MM-dd");

  // Al mount (in modifica) carica i dati
  useEffect(() => {
    if (action === "modifica" && id) {
      const filteredTasks = task.filter((t) => t.eventoId === id);
      const url = `${urleventi}/${id}`;
      dispatch(allGet(url, token)).then((singoloEvento) => {
        if (singoloEvento) {
          setEvento({
            ...singoloEvento,
            tasks: filteredTasks,
          });
        } else {
          console.error("Errore nel caricamento dell'evento");
        }
      });
    }
  }, [action, id, dispatch, token, task]);

  // Gestione generica dei campi testuali
  const handleChange = (field: keyof Eventi, value: any) => {
    setEvento((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Per gestire la data con react-datepicker
  const handleDateChange = (field: keyof Eventi, value: Date | null) => {
    setEvento((prev) => ({
      ...prev,
      [field]: value ? dateToString(value) : "",
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleAddTask = (newTask: Task) => {
    setEvento((prev) => ({
      ...prev,
      tasks: [...prev.tasks, newTask],
    }));
  };

  const updateTask = (updatedTask: Task) => {
    setEvento((prevEvento) => ({
      ...prevEvento,
      tasks: prevEvento.tasks.map((t) => (t.id === updatedTask.id ? updatedTask : t)),
    }));
  };

  const setArrayTask = (arrayTask: Task[]) => {
    setEvento((prevEvento) => ({
      ...prevEvento,
      tasks: arrayTask,
    }));
  };

  // Funzione per validare i campi obbligatori
  const validateFields = () => {
    const newErrors = {
      titolo: !evento.titolo.trim(),
    };
    setErrors(newErrors);

    return !Object.values(newErrors).some((val) => val);
  };

  const handleSubmit = () => {
    // Se la validazione fallisce, esco
    if (!validateFields()) {
      showAlert("danger", "Errore", "Compila i campi obbligatori evidenziati in rosso.", "bottom-end", 3000);
      return; // Non chiudere la pagina
    }

    // Se la validazione passa, proseguo con Post/Put
    if (action === "crea") {
      dispatch(allPost(urleventi, token, evento, showAlert)).then((nuovoId) => {
        if (nuovoId) {
          showAlert("success", "Creato", "Evento creato con successo.", "bottom-end", 3000);

          // Se l'evento ha tasks, li salvo
          evento.tasks.forEach((ts) => {
            const updatedTask = { ...ts, eventoId: nuovoId };
            dispatch(allPost(urltask, token, updatedTask, showAlert)).then(() => {
              console.log("Task aggiunto:", updatedTask);
            });
          });
          navigate("/eventi"); // Chiudo e torno alla lista
        } else {
          console.error("Creazione dell'evento fallita");
        }
      });
    } else if (action === "modifica" && id) {
      const url = `${urleventi}/${id}`;
      dispatch(allPut(url, token, evento, showAlert)).then((updatedEvento) => {
        if (updatedEvento) {
          console.log("Evento aggiornato:", updatedEvento);
          showAlert("success", "Salvato", "Evento aggiornato con successo.", "bottom-end", 3000);
          setIsEditing(false);

          navigate("/eventi");
        } else {
          console.error("Modifica dell'evento fallita");
        }
      });
    }
  };

  const handleClose = () => {
    navigate("/eventi");
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  return (
    <div className="home-container">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center mb-5">
        <h2 className="titolo-header-Crea-Edit">
          Evento/<b>{action === "crea" ? "Nuovo Evento" : evento.titolo}</b>
        </h2>
        <div className="d-flex">
          {isEditing ? (
            <Button onClick={handleSubmit} className="ms-2 button d-flex px-3">
              <i className="pi pi-save me-2"></i>
              <p className="p-0 m-0">Salva</p>
            </Button>
          ) : (
            <Button className="ms-2 button d-flex px-3" onClick={handleEdit}>
              <i className="pi pi-pencil me-2"></i>
              <p className="p-0 m-0">Modifica</p>
            </Button>
          )}

          {action === "modifica" && (
            <Button onClick={() => setIsDeleteModalOpen(true)} className="ms-2 button d-flex px-3">
              <i className="pi pi-trash me-2"></i>
              <p className="p-0 m-0">Elimina</p>
            </Button>
          )}
          <Button onClick={handleClose} className="ms-2 button d-flex px-3">
            <i className="pi pi-times me-2"></i>
            <p className="p-0 m-0">Chiudi</p>
          </Button>
        </div>
      </div>

      <Row className="g-3">
        <Col xs={12} className="px-0">
          <div className="create-edit-top-eventi">
            <Row className="row-form">
              <Col lg={4} className="colonna-form">
                {/* Titolo (obbligatorio) */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Titolo *</Form.Label>
                  <InputText
                    className="w-50 input-crea-edit"
                    value={evento.titolo}
                    onChange={(e) => {
                      handleChange("titolo", e.target.value);
                      setErrors((prev) => ({ ...prev, titolo: false }));
                    }}
                    placeholder="Inserisci titolo"
                    disabled={!isEditing}
                    style={errors.titolo ? { border: "1px solid red" } : undefined}
                  />
                </Form.Group>

                {/* Data Esecuzione */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Data Esecuzione</Form.Label>
                  <div className="w-50">
                    <DatePicker
                      className="w-100 input-crea-edit  cursor-pointer"
                      selected={evento.dataEsecuzione ? new Date(evento.dataEsecuzione) : null}
                      onChange={(date) => handleDateChange("dataEsecuzione", date)}
                      placeholderText="Seleziona data esecuzione"
                      dateFormat="dd/MM/yyyy"
                      locale="it"
                      disabled={!isEditing}
                    />
                  </div>
                </Form.Group>

                {/* Data Inizio (dataOra) (obbligatorio) */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Data Inizio *</Form.Label>
                  <div className="w-50">
                    <DatePicker
                      className="w-100 input-crea-edit cursor-pointer"
                      selected={evento.dataOra ? new Date(evento.dataOra) : null}
                      onChange={(date) => {
                        handleDateChange("dataOra", date);
                        setErrors((prev) => ({ ...prev, dataOra: false }));
                      }}
                      placeholderText="Seleziona data inizio"
                      dateFormat="dd/MM/yyyy"
                      locale="it"
                      disabled={!isEditing}
                    />
                  </div>
                </Form.Group>

                {/* Scadenza */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Scadenza</Form.Label>
                  <div className="w-50">
                    <DatePicker
                      className="w-100 input-crea-edit cursor-pointer"
                      selected={evento.scadenza ? new Date(evento.scadenza) : null}
                      onChange={(date) => handleDateChange("scadenza", date)}
                      placeholderText="Seleziona scadenza"
                      dateFormat="dd/MM/yyyy"
                      locale="it"
                      disabled={!isEditing}
                    />
                  </div>
                </Form.Group>
              </Col>

              <Col lg={4} className="colonna-centrale colonna-form">
                {/* Committente */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Committente</Form.Label>
                  <Dropdown
                    disabled={!isEditing || !!evento.cantiereId || !!evento.contrattoQuadroId}
                    editable
                    className="w-50 input-crea-edit p-0"
                    value={evento.committenteId}
                    options={committenti
                      .filter((committente) =>
                        committente.ragioneSociale.toLowerCase().includes(committenteSearchText.toLowerCase())
                      )
                      .map((committente) => ({
                        label: committente.ragioneSociale,
                        value: committente.id,
                      }))}
                    onChange={(e) => handleChange("committenteId", e.value)}
                    onInput={(e) => {
                      const inputValue = (e.target as HTMLInputElement).value;
                      setCommittenteSearchText(inputValue);
                    }}
                    placeholder="Seleziona committente"
                  />
                </Form.Group>

                {/* Contratto Quadro */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Contratto Quadro</Form.Label>
                  <Dropdown
                    disabled={!isEditing || !!evento.cantiereId || !!evento.committenteId}
                    editable
                    className="w-50 input-crea-edit p-0"
                    value={evento.contrattoQuadroId}
                    options={contratti
                      .filter((contratto) => contratto.codice.toLowerCase().includes(contrattoSearchText.toLowerCase()))
                      .map((contratto) => ({
                        label: contratto.codice,
                        value: contratto.id,
                      }))}
                    onChange={(e) => handleChange("contrattoQuadroId", e.value)}
                    onInput={(e) => {
                      const inputValue = (e.target as HTMLInputElement).value;
                      setContrattoSearchText(inputValue);
                    }}
                    placeholder="Seleziona contratto"
                  />
                </Form.Group>

                {/* Cantiere */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Cantiere</Form.Label>
                  <Dropdown
                    disabled={!isEditing || !!evento.committenteId || !!evento.contrattoQuadroId}
                    editable
                    className="w-50 input-crea-edit p-0"
                    value={evento.cantiereId}
                    options={cantieri
                      .filter((c) => c.nome.toLowerCase().includes(cantiereSearchText.toLowerCase()))
                      .map((c) => ({
                        label: c.nome,
                        value: c.id,
                      }))}
                    onChange={(e) => handleChange("cantiereId", e.value)}
                    onInput={(e) => {
                      const inputValue = (e.target as HTMLInputElement).value;
                      setCantiereSearchText(inputValue);
                    }}
                    placeholder="Seleziona cantiere"
                  />
                </Form.Group>
              </Col>

              <Col lg={4} className="colonna-form">
                {/* Descrizione */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex flex-column">
                  <Form.Label className="mb-3">
                    <b>Descrizione:</b>
                  </Form.Label>
                  <InputTextarea
                    style={{ backgroundColor: "#f5f5f5" }}
                    rows={5}
                    cols={30}
                    value={evento.descrizione}
                    onChange={(e) => handleChange("descrizione", e.target.value)}
                    disabled={!isEditing}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Col>

        {/* File Upload Section */}
        <Col lg={5} className="ps-0">
          <Container className="create-edit-under">
            {action === "modifica" ? (
              <FileUploadComponent folderType="eventi" folderKey={evento.titolo} />
            ) : (
              <>
                <h5 className="text-start m-0">File</h5>
                <br />
                <p>Per caricare un file, devi prima aver creato l'evento</p>
              </>
            )}
          </Container>
        </Col>

        {/* Task Section */}
        <Col lg={7} className="pe-0">
          <Container className="create-edit-under Container-flex-bet">
            <ModalFormTask
              arrayTasks={evento.tasks}
              onAddTask={handleAddTask}
              updateTask={updateTask}
              setArrayTask={setArrayTask}
              action={action}
              nome={evento.titolo}
              idPrincipale={id || ""}
              type="eventi"
            />
          </Container>
        </Col>
      </Row>

      <DeleteModal
        type="evento"
        idObj={evento.id}
        matchIdentifier={evento.titolo}
        show={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDeleteSuccess={handleClose}
      />
    </div>
  );
};

export default FormEventi;
